import { useState, useEffect } from 'react'
import { supabase } from '../../../../../shared/lib/supabase'
import PolicyLibrarySelectorModal from '../../../../policies/components/PolicyLibrarySelectorModal'
import { usePolicyInventory } from '../../../../policies/hooks/usePolicyInventory'
import { deriveOrgSize } from '../../../../../shared/utils/policyInventoryEngine'
import { REGULATORY_FRAMEWORK_OPTIONS, getSubCategory } from '../../../../../shared/data/industries'
import ClientPoliciesAdmin from '../../../../policies/pages/ClientPoliciesAdmin'

const FRAMEWORK_LABEL_MAP = Object.fromEntries(
  REGULATORY_FRAMEWORK_OPTIONS.map(f => [f.value, f.label])
)

const subTabs = [
  {
    id: 'recommended',
    label: 'Recommended Policies',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    )
  },
  {
    id: 'library',
    label: 'Policy Library',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    )
  },
  {
    id: 'enrichment',
    label: 'Enrichment Context',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
      </svg>
    )
  }
]

// ── Enrichment Context Sub-Tab ───────────────────────────────────────────────

function ContextFieldCard({ label, originalValue, overrideValue, enabled, onToggle, onOverride, onClearOverride }) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(overrideValue || '')
  const displayValue = overrideValue || originalValue
  const isOverridden = overrideValue && overrideValue !== originalValue

  const handleSaveEdit = () => {
    if (editValue.trim() && editValue.trim() !== originalValue) {
      onOverride(editValue.trim())
    } else {
      onClearOverride()
    }
    setEditing(false)
  }

  return (
    <div className={`p-3 rounded-lg border transition-colors ${enabled ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800' : 'border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-800/50 opacity-60'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</p>
            {isOverridden && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Override</span>
            )}
          </div>
          {editing ? (
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="text"
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                className="flex-1 px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(); if (e.key === 'Escape') setEditing(false) }}
              />
              <button onClick={handleSaveEdit} className="text-xs px-2 py-1.5 rounded bg-primary-600 text-white hover:bg-primary-700 transition-colors">Save</button>
              <button onClick={() => setEditing(false)} className="text-xs px-2 py-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">Cancel</button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-0.5">
              <p className={`text-sm ${enabled ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 line-through'}`}>{displayValue}</p>
              {enabled && (
                <button
                  onClick={() => { setEditValue(displayValue); setEditing(true) }}
                  className="p-0.5 text-gray-300 hover:text-primary-500 dark:text-gray-600 dark:hover:text-primary-400 transition-colors"
                  title="Edit override value"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </button>
              )}
              {isOverridden && enabled && (
                <button
                  onClick={onClearOverride}
                  className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                  title="Reset to original"
                >
                  Reset
                </button>
              )}
            </div>
          )}
          {isOverridden && !editing && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 line-through">{originalValue}</p>
          )}
        </div>
        <button
          onClick={onToggle}
          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out mt-1 ${enabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
          role="switch"
          aria-checked={enabled}
        >
          <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabled ? 'translate-x-4' : 'translate-x-0'}`} />
        </button>
      </div>
    </div>
  )
}

function EnrichmentContextPanel({ client }) {
  const [technologies, setTechnologies] = useState([])
  const [loading, setLoading] = useState(true)
  const [config, setConfig] = useState({ disabled: {}, overrides: {}, customNotes: '' })
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [dirty, setDirty] = useState(false)

  const contextFields = [
    { key: 'name', label: 'Organization Name', value: client?.name },
    { key: 'industry', label: 'Industry', value: client?.industry || null },
    { key: 'industry_sub_category', label: 'Sub-Industry', value: (() => {
      const sub = client?.industrySubCategory || client?.industry_sub_category
      if (!sub) return null
      return getSubCategory(client?.industry, sub)?.label || sub
    })() },
    { key: 'staffing_size', label: 'Organization Size', value: (client?.staffingSize || client?.staffing_size) ? `${client.staffingSize || client.staffing_size} employees` : null },
    { key: 'annual_revenue', label: 'Annual Revenue', value: client?.annualRevenue || client?.annual_revenue || null },
    { key: 'it_team_size', label: 'IT Team Size', value: (client?.itTeamSize || client?.it_team_size) ? `${client.itTeamSize || client.it_team_size} people` : null },
    { key: 'geographical_presence', label: 'Geographic Presence', value: client?.geographicalPresence || client?.geographical_presence || null },
  ]

  // Derived assessment data
  const completedAssessments = (client?.assessments || [])
    .filter(a => a.status === 'completed')
    .sort((a, b) => new Date(b.completedAt || b.completed_at || 0) - new Date(a.completedAt || a.completed_at || 0))
  const latestScores = completedAssessments[0]?.scores || null
  const maturityLevel = latestScores?.overall?.maturityLevel?.label
  const overallScore = latestScores?.overall?.score
  const maturityDisplay = maturityLevel ? `${maturityLevel}${overallScore != null ? ` (${overallScore}/100)` : ''}` : null
  const riskAppetite = latestScores?.riskAppetite?.averageAppetite != null
    ? (() => { const s = latestScores.riskAppetite.averageAppetite; return s <= 2 ? 'Conservative' : s <= 3 ? 'Moderate' : s <= 4 ? 'Aggressive' : 'Very Aggressive' })()
    : null

  const frameworks = Array.isArray(client?.regulatoryFrameworks)
    ? client.regulatoryFrameworks
    : Array.isArray(client?.regulatory_frameworks) ? client.regulatory_frameworks : []

  useEffect(() => {
    if (!client?.id) return
    const load = async () => {
      setLoading(true)
      const [techRes, configRes] = await Promise.all([
        supabase.from('client_technology_stack').select('name').eq('client_id', client.id),
        supabase.from('clients').select('notes').eq('id', client.id).single()
      ])
      setTechnologies((techRes.data || []).map(t => t.name))
      // Load saved enrichment config from client notes (stored as JSON in notes field under enrichment_config key)
      try {
        const notes = configRes.data?.notes
        if (notes) {
          const parsed = JSON.parse(notes)
          if (parsed?.enrichment_config) setConfig(parsed.enrichment_config)
        }
      } catch { /* notes is not JSON, ignore */ }
      setLoading(false)
    }
    load()
  }, [client?.id])

  useEffect(() => {
    if (saveSuccess) {
      const t = setTimeout(() => setSaveSuccess(false), 3000)
      return () => clearTimeout(t)
    }
  }, [saveSuccess])

  const isEnabled = (key) => !config.disabled[key]

  const toggleField = (key) => {
    setConfig(prev => ({ ...prev, disabled: { ...prev.disabled, [key]: !prev.disabled[key] } }))
    setDirty(true)
  }

  const setOverride = (key, value) => {
    setConfig(prev => ({ ...prev, overrides: { ...prev.overrides, [key]: value } }))
    setDirty(true)
  }

  const clearOverride = (key) => {
    setConfig(prev => {
      const next = { ...prev.overrides }
      delete next[key]
      return { ...prev, overrides: next }
    })
    setDirty(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      // Read current notes, merge enrichment_config in
      const { data: current } = await supabase.from('clients').select('notes').eq('id', client.id).single()
      let notesObj = {}
      try { notesObj = JSON.parse(current?.notes || '{}') } catch { notesObj = { _raw: current?.notes } }
      notesObj.enrichment_config = config

      await supabase.from('clients').update({ notes: JSON.stringify(notesObj) }).eq('id', client.id)
      setDirty(false)
      setSaveSuccess(true)
    } catch (err) {
      console.error('Failed to save enrichment config:', err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Client Profile Context */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">AI Enrichment Context</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Control what client data is sent to Claude during policy enrichment. Toggle fields off to exclude, or click the edit icon to override a value.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {saveSuccess && (
              <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved
              </span>
            )}
            {dirty && (
              <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {/* Client profile fields — read-only with toggles */}
          {contextFields.map(field => {
            if (!field.value) return null
            return (
              <div key={field.key} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${isEnabled(field.key) ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800' : 'border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-800/50 opacity-60'}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{field.label}</p>
                  <p className={`text-sm mt-0.5 ${isEnabled(field.key) ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 line-through'}`}>{field.value}</p>
                </div>
                <button
                  onClick={() => toggleField(field.key)}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ml-4 ${isEnabled(field.key) ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                  role="switch"
                  aria-checked={isEnabled(field.key)}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isEnabled(field.key) ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            )
          })}

          {/* Assessment-derived fields — read-only with toggles */}
          {maturityDisplay && (
            <div className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${isEnabled('maturity') ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800' : 'border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-800/50 opacity-60'}`}>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Security Maturity</p>
                <p className={`text-sm mt-0.5 ${isEnabled('maturity') ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 line-through'}`}>{maturityDisplay}</p>
              </div>
              <button
                onClick={() => toggleField('maturity')}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ml-4 ${isEnabled('maturity') ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                role="switch"
                aria-checked={isEnabled('maturity')}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isEnabled('maturity') ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
          )}

          {riskAppetite && (
            <div className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${isEnabled('risk_appetite') ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800' : 'border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-800/50 opacity-60'}`}>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Risk Appetite</p>
                <p className={`text-sm mt-0.5 ${isEnabled('risk_appetite') ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 line-through'}`}>{riskAppetite}</p>
              </div>
              <button
                onClick={() => toggleField('risk_appetite')}
                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ml-4 ${isEnabled('risk_appetite') ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                role="switch"
                aria-checked={isEnabled('risk_appetite')}
              >
                <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isEnabled('risk_appetite') ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
          )}

          {/* Editable fields — security tools and custom notes can be overridden */}
          {technologies.length > 0 && (
            <ContextFieldCard
              label={`Security Tools (${technologies.length})`}
              originalValue={technologies.join(', ')}
              overrideValue={config.overrides.technologies}
              enabled={isEnabled('technologies')}
              onToggle={() => toggleField('technologies')}
              onOverride={(val) => setOverride('technologies', val)}
              onClearOverride={() => clearOverride('technologies')}
            />
          )}

          {frameworks.length > 0 && (
            <div className={`p-3 rounded-lg border transition-colors ${isEnabled('frameworks') ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800' : 'border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-800/50 opacity-60'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Regulatory Frameworks</p>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {frameworks.map(f => (
                      <span key={f} className={`text-xs px-2 py-0.5 rounded ${isEnabled('frameworks') ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'}`}>
                        {FRAMEWORK_LABEL_MAP[f] || f}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => toggleField('frameworks')}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out mt-1 ${isEnabled('frameworks') ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                  role="switch"
                  aria-checked={isEnabled('frameworks')}
                >
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isEnabled('frameworks') ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Edit source note */}
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Client profile fields (organization name, industry, size, revenue, geography) are pulled from the client record. To update these values, edit the client profile from the <span className="font-medium text-gray-500 dark:text-gray-400">Overview</span> tab. Assessment-derived fields (maturity, risk appetite) update automatically when assessments are completed.
        </p>
      </div>

      {/* Custom Context Notes */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700/30 pb-2 mb-2">Additional Context for AI</h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
          Add any additional instructions or context that should be included when enriching policies for this client. This is appended to every enrichment prompt.
        </p>
        <textarea
          value={config.customNotes || ''}
          onChange={e => { setConfig(prev => ({ ...prev, customNotes: e.target.value })); setDirty(true) }}
          placeholder="e.g., This client is undergoing an acquisition and needs policies that address multi-entity data sharing. They have a zero-trust network architecture with Zscaler as the primary proxy."
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
        />
      </div>

      {/* Info note */}
      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-700 dark:text-blue-400 flex items-start gap-2">
        <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
        </svg>
        <span>Disabled fields are excluded from AI enrichment. Overridden values replace the original client profile data in the AI prompt without changing the client record. Custom notes are appended as additional context.</span>
      </div>
    </div>
  )
}

// ── Recommended Policies Sub-Tab ─────────────────────────────────────────────

function RecommendedPoliciesPanel({ client }) {
  const [copyingAll, setCopyingAll] = useState(false)
  const [copyingId, setCopyingId] = useState(null)
  const [selectedPolicy, setSelectedPolicy] = useState(null)
  const [activeFilter, setActiveFilter] = useState('All')

  const {
    recommendations,
    templateMatches,
    stats,
    copyTemplateToClient,
    copyAllToClient,
    recalculate
  } = usePolicyInventory(client)

  const frameworks = Array.isArray(client?.regulatoryFrameworks) ? client.regulatoryFrameworks : []
  const sizeCategory = deriveOrgSize(client.staffingSize || client.staffing_size, client.annualRevenue || client.annual_revenue)

  const filteredRecs = recommendations.filter(r => {
    if (activeFilter === 'All') return true
    if (activeFilter === 'Gap') return r.status === 'gap' && !r.is_consolidated
    if (activeFilter === 'In Progress') return r.status === 'in_progress' && !r.is_consolidated
    if (activeFilter === 'Adopted') return r.status === 'adopted' && !r.is_consolidated
    return true
  })

  const copyableCount = recommendations.filter(r =>
    !r.is_consolidated && !r.linked_client_policy_id && templateMatches.get(r.catalog_policy_id)
  ).length

  const handleCopyOne = async (catalogPolicyId) => {
    setCopyingId(catalogPolicyId)
    try {
      await copyTemplateToClient(catalogPolicyId)
    } catch (err) {
      console.error('Copy failed:', err)
    } finally {
      setCopyingId(null)
    }
  }

  const handleCopyAll = async () => {
    setCopyingAll(true)
    try {
      await copyAllToClient()
    } catch (err) {
      console.error('Copy all failed:', err)
    } finally {
      setCopyingAll(false)
    }
  }

  if (recommendations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
        <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">No policy recommendations yet.</p>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Complete a security assessment to generate a recommended policy set based on your client's industry and regulatory requirements.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Recommended Policy Set</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Based on profile: {client.industry || 'Any industry'} &middot; {sizeCategory}{frameworks.length > 0 ? ` \u00B7 ${frameworks.map(f => FRAMEWORK_LABEL_MAP[f] || f).join(' + ')}` : ''}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {stats.totalRecommended} policies recommended{stats.totalConsolidated > 0 && ` (${stats.totalConsolidated} consolidated for your organization size)`}
          </p>
        </div>

        {/* Stats badges */}
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
            Adopted: {stats.adopted}
          </span>
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
            In Progress: {stats.inProgress}
          </span>
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
            Gap: {stats.gap}
          </span>
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between mt-4 mb-3">
          <div className="flex items-center gap-2">
            {['All', 'Gap', 'In Progress', 'Adopted'].map(filter => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  activeFilter === filter
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={recalculate}
              title="Recalculate recommendations"
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            {copyableCount > 0 && (
              <button
                onClick={handleCopyAll}
                disabled={copyingAll}
                className="btn-primary text-sm disabled:opacity-50"
              >
                {copyingAll ? 'Copying...' : `Copy All Available (${copyableCount})`}
              </button>
            )}
          </div>
        </div>

        {/* Policy table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Policy</th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">Domain</th>
                <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Required By</th>
                <th className="text-right py-2.5 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredRecs.map(rec => {
                const catalog = rec._catalog || {}
                const isConsolidated = rec.is_consolidated
                const parentName = isConsolidated
                  ? recommendations.find(r => r.catalog_policy_id === rec.consolidated_into)?._catalog?.name || rec.consolidated_into
                  : null
                const hasTemplate = templateMatches.get(rec.catalog_policy_id)
                const isCopying = copyingId === rec.catalog_policy_id

                return (
                  <tr
                    key={rec.id}
                    onClick={() => setSelectedPolicy(rec)}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors cursor-pointer ${isConsolidated ? 'opacity-60' : ''}`}
                  >
                    <td className="py-2.5 px-3">
                      {isConsolidated ? (
                        <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">Consolidated</span>
                      ) : rec.status === 'adopted' ? (
                        <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Adopted</span>
                      ) : rec.status === 'in_progress' ? (
                        <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">In Progress</span>
                      ) : (
                        <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Gap</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className={isConsolidated ? 'italic' : ''}>
                        <p className={`font-medium ${isConsolidated ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                          {catalog.name}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {isConsolidated ? `\u2192 Merged into ${parentName}` : catalog.category}
                        </p>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 hidden lg:table-cell">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{catalog.domain}</span>
                    </td>
                    <td className="py-2.5 px-3 hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(catalog.requiredBy || []).slice(0, 2).map(f => (
                          <span key={f} className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">{f}</span>
                        ))}
                        {(catalog.requiredBy || []).length > 2 && (
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                            +{catalog.requiredBy.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      {isConsolidated ? null : rec.linked_client_policy_id ? (
                        <span className="text-xs font-medium text-green-600 dark:text-green-400">Assigned</span>
                      ) : hasTemplate ? (
                        <button
                          onClick={e => { e.stopPropagation(); handleCopyOne(rec.catalog_policy_id) }}
                          disabled={isCopying}
                          className="text-xs font-medium px-3 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
                        >
                          {isCopying ? 'Copying...' : 'Copy from Library'}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500 italic">No template yet</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Policy Detail Modal */}
      {selectedPolicy && (() => {
        const catalog = selectedPolicy._catalog || {}
        const isConsolidated = selectedPolicy.is_consolidated
        const parentName = isConsolidated
          ? recommendations.find(r => r.catalog_policy_id === selectedPolicy.consolidated_into)?._catalog?.name
          : null
        const hasTemplate = templateMatches.get(selectedPolicy.catalog_policy_id)
        const isCopying = copyingId === selectedPolicy.catalog_policy_id

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPolicy(null)}>
            <div className="absolute inset-0 bg-black/50" />
            <div
              className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-xl">
                <div className="flex items-start justify-between">
                  <div className="flex-1 mr-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{catalog.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{catalog.category} &middot; {catalog.domain}</p>
                  </div>
                  <button onClick={() => setSelectedPolicy(null)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="px-6 py-5 space-y-5">
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Status</p>
                  {isConsolidated ? (
                    <div>
                      <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">Consolidated</span>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                        This policy has been merged into <span className="font-medium text-gray-700 dark:text-gray-300">{parentName}</span> based on your organization size.
                      </p>
                    </div>
                  ) : selectedPolicy.status === 'adopted' ? (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Adopted</span>
                  ) : selectedPolicy.status === 'in_progress' ? (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">In Progress</span>
                  ) : (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Gap</span>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Description</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{catalog.description}</p>
                </div>
                {(catalog.requiredBy || []).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Required By</p>
                    <div className="flex flex-wrap gap-1.5">
                      {catalog.requiredBy.map(f => (
                        <span key={f} className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">{f}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Library Template</p>
                  {hasTemplate ? (
                    <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Template available in admin policy library
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                      No template available yet
                    </p>
                  )}
                </div>
              </div>

              {!isConsolidated && (
                <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 rounded-b-xl flex justify-end gap-3">
                  {selectedPolicy.linked_client_policy_id ? (
                    <span className="text-sm text-green-600 dark:text-green-400">Already assigned to client</span>
                  ) : hasTemplate ? (
                    <button
                      onClick={() => { handleCopyOne(selectedPolicy.catalog_policy_id); setSelectedPolicy(null) }}
                      disabled={isCopying}
                      className="btn-primary text-sm disabled:opacity-50"
                    >
                      {isCopying ? 'Copying...' : 'Copy from Library'}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// ── Main PoliciesTab ─────────────────────────────────────────────────────────

function PoliciesTab({ client }) {
  const [activeSubTab, setActiveSubTab] = useState('recommended')
  const [clientPolicies, setClientPolicies] = useState([])
  const [loading, setLoading] = useState(true)
  const [showLibrarySelector, setShowLibrarySelector] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const refreshPolicies = async () => {
    const { data } = await supabase
      .from('client_policies')
      .select('*')
      .eq('client_id', client.id)
      .order('domain', { ascending: true })
    setClientPolicies(data || [])
  }

  useEffect(() => {
    if (!client.id) return
    const fetchPolicies = async () => {
      setLoading(true)
      await refreshPolicies()
      setLoading(false)
    }
    fetchPolicies()
  }, [client.id])

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(''), 5000)
      return () => clearTimeout(t)
    }
  }, [successMessage])

  const published = clientPolicies.filter(p => p.status === 'published')
  const drafts = clientPolicies.filter(p => p.status === 'draft')

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  const existingPolicyDomains = clientPolicies.map(p => p.domain)

  return (
    <div className="space-y-4">
      {/* Success toast */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {successMessage}
        </div>
      )}

      {/* Page header */}
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Policies</h1>
        <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-0.5">
          Policy recommendations are generated based on {client?.name || "this client"}'s industry, size, regulatory requirements, and compliance frameworks. When copied from the admin library, policies are automatically enriched by AI to reflect the organization's specific context, terminology, and operational details.
        </p>
      </div>

      {/* Sub-tab navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-6">
          {subTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`pb-3 text-sm font-medium transition-colors relative ${
                activeSubTab === tab.id
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
              {activeSubTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Sub-tab content */}
      {activeSubTab === 'recommended' && (
        <RecommendedPoliciesPanel client={client} />
      )}

      {activeSubTab === 'library' && (
        <ClientPoliciesAdmin clientId={client.id} embedded={true} />
      )}

      {activeSubTab === 'enrichment' && (
        <EnrichmentContextPanel client={client} />
      )}

      {/* Policy Library Selector Modal */}
      {showLibrarySelector && (
        <PolicyLibrarySelectorModal
          clientId={client.id}
          clientData={client}
          existingPolicyDomains={existingPolicyDomains}
          onClose={() => setShowLibrarySelector(false)}
          onCopyComplete={(count) => {
            setShowLibrarySelector(false)
            refreshPolicies()
            setSuccessMessage(`${count} polic${count === 1 ? 'y' : 'ies'} copied. Enrichment is running in the background.`)
          }}
        />
      )}
    </div>
  )
}

export default PoliciesTab
